import React, { createContext, useState, useCallback, useContext, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { AuthContext } from './AuthContext';
import { PusherContext } from '../providers/PusherProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Rules whose violations are scoped to a single calendar day (ET).
// Alerts for these rules must have been created today (ET) to apply a lockout.
const DAY_BASED_RULES = new Set(['max_trades_per_day', 'max_loss_per_day', 'profit_target_per_day']);
const ET_TIMEZONE = 'America/New_York';

/**
 * Returns false for day-based rule alerts whose starts_at/created_at falls on a previous ET
 * calendar date. Prevents a post-market-close alert from carrying over into the
 * next trading day when expires_at was incorrectly set into the future.
 */
function isAlertRelevantToday(alert) {
  if (!DAY_BASED_RULES.has(alert.rule_type)) return true;
  const startsAt = alert.starts_at || alert.created_at;
  if (!startsAt) return true;
  const todayET = moment().tz(ET_TIMEZONE).format('YYYY-MM-DD');
  const alertDateET = moment(startsAt).tz(ET_TIMEZONE).format('YYYY-MM-DD');
  return todayET === alertDateET;
}

function sameAlertId(a, b) {
  return String(a) === String(b);
}

export const EnforcementAlertsContext = createContext(null);

/**
 * Context for unified enforcement alerts (replaces LossLimitContext alert functionality).
 * Fetches all unacknowledged enforcement alerts for the user on mount and keeps them
 * in sync via Pusher enforcement-alert events.
 */
export const EnforcementAlertsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const pusher = useContext(PusherContext);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchingRef = useRef(false);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${user?.token}`
  }), [user?.token]);

  const fetchPendingAlerts = useCallback(async () => {
    if (!user?.token || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/enforcement/alerts/pending`, {
        headers: getHeaders(),
        cache: 'no-store',
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setPendingAlerts(json.pendingAlerts || []);
        }
      }
    } catch (err) {
      console.error('[EnforcementAlertsContext] Error fetching pending alerts:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [user?.token, getHeaders]);

  const acknowledgeAlert = useCallback(async (alertId, userAction) => {
    if (!user?.token) return { success: false };
    try {
      const res = await fetch(`${BACKEND_URL}/enforcement/alerts/${alertId}/acknowledge`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ userAction }),
        cache: 'no-store',
      });
      const json = await res.json();
      if (res.ok && json.success) {
        // Prefer the row returned by the server (includes dismissed_at). Keep the row in
        // pendingAlerts so lockout checks still work; PositionLossListener hides dismissed popups.
        const updated = json.alert;
        setPendingAlerts(prev =>
          prev.map(a => (sameAlertId(a.id, alertId)
            ? (updated ? { ...a, ...updated } : { ...a, dismissed_at: new Date().toISOString() })
            : a))
        );
        return { success: true };
      }
      return { success: false, error: json.error };
    } catch (err) {
      console.error('[EnforcementAlertsContext] Error acknowledging alert:', err);
      return { success: false, error: err.message };
    }
  }, [user?.token, getHeaders]);

  /**
   * Returns true if the account has an active lockout.
   *
   * dismissed_at is intentionally NOT checked here: dismissing the alert popup only
   * silences the notification, it must never lift an enforced trading restriction early.
   * Only expires_at/archived_at (the latter is filtered server-side) end a lockout.
   *
   * Day-based rules (max_trades_per_day, max_loss_per_day) are only applied when the alert
   * was created on today's ET calendar date, preventing post-market-close alerts from
   * carrying over into the next trading day.
   */
  const isAccountLockedOut = useCallback((accountId) => {
    const now = new Date();
    return pendingAlerts.some(a =>
      a.account_id === accountId &&
      (!a.expires_at || new Date(a.expires_at) > now) &&
      isAlertRelevantToday(a)
    );
  }, [pendingAlerts]);

  /**
   * Returns the most recent active lockout alert for the account, or null.
   *
   * dismissed_at is intentionally NOT checked (see isAccountLockedOut above).
   *
   * Day-based rules (max_trades_per_day, max_loss_per_day) are only applied when the alert
   * was created on today's ET calendar date.
   */
  const getAccountLockout = useCallback((accountId) => {
    const now = new Date();
    return pendingAlerts.find(a =>
      a.account_id === accountId &&
      (!a.expires_at || new Date(a.expires_at) > now) &&
      isAlertRelevantToday(a)
    ) || null;
  }, [pendingAlerts]);

  // Fetch on login / token change
  useEffect(() => {
    if (user?.token) {
      fetchPendingAlerts();
    } else {
      setPendingAlerts([]);
    }
  }, [user?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to Pusher enforcement-alert events
  useEffect(() => {
    if (!pusher || !user?.id) return;

    const channelName = `user-${user.id}-alerts`;
    let channel = pusher.channel(channelName);
    if (!channel) {
      channel = pusher.subscribe(channelName);
    }

    const handleEnforcementAlert = () => {
      console.log('[EnforcementAlertsContext] Received enforcement-alert, refreshing alerts');
      fetchPendingAlerts();
    };

    channel.bind('enforcement-alert', handleEnforcementAlert);

    return () => {
      if (channel) {
        channel.unbind('enforcement-alert', handleEnforcementAlert);
      }
    };
  }, [pusher, user?.id, fetchPendingAlerts]);

  const value = {
    pendingAlerts,
    loading,
    fetchPendingAlerts,
    acknowledgeAlert,
    isAccountLockedOut,
    getAccountLockout
  };

  return (
    <EnforcementAlertsContext.Provider value={value}>
      {children}
    </EnforcementAlertsContext.Provider>
  );
};

export const useEnforcementAlerts = () => {
  const context = useContext(EnforcementAlertsContext);
  if (!context) {
    throw new Error('useEnforcementAlerts must be used within EnforcementAlertsProvider');
  }
  return context;
};

export default EnforcementAlertsContext;
